import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack,
  Search,
  EmojiEvents,
  ArrowForward,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../context/AppContext';
import { useClasificacion, useJornadas } from '../hooks/queries';
import { useCreateLiveTagSession } from '../hooks/queries/useLiveTag';
import { useButtonPanels } from '../hooks/queries/useButtonPanels';
import FixtureCard, { hasFinalScore } from '../components/partidos/FixtureCard';
import { Card, CardContent, Button } from '../design-system/components';
import { Skeleton, EmptyState } from '../design-system/components';
import Filters from '../components/Filters';
import usePageTitle from '../hooks/usePageTitle';
import { buildActaNavigation } from '../utils/matchNavigation';
import { countPanelActions, normalizePanelEntries } from '../utils/liveTagPanel';
import { filterMatchdays, getFixtureHighlights, getMatchdayNumber } from '../utils/matchdays';
import './PartidosPageModern.css';

/**
 * PartidosPageModern - Página modernizada de partidos
 * Lista de partidos agrupados por jornada con filtros y búsqueda
 */

const SESSIONS_STORAGE_KEY = 'liveTag:sessions';
const LIVE_TAG_STORAGE_KEY = 'liveTag:selectedMatch';

function persistSession(matchData) {
  const existing = JSON.parse(localStorage.getItem(SESSIONS_STORAGE_KEY) || '[]');
  const nextSessions = existing.filter((session) => {
    const sessionId = session.id || session.sessionId || session.liveTagMatchId;
    return sessionId !== matchData.liveTagMatchId && sessionId !== matchData.sessionId;
  });
  nextSessions.unshift(matchData);
  localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(nextSessions));
}

const PartidosPageModern = ({ favorite }) => {
  usePageTitle('Partidos');
  const { t } = useTranslation('matches');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { filters } = useAppContext();
  const selectedJornada = searchParams.get('jornada') || 'all';
  const searchTerm = searchParams.get('q') || '';
  const status = ['finished', 'scheduled'].includes(searchParams.get('status')) ? searchParams.get('status') : 'all';
  const onlyFavorite = searchParams.get('team') === 'favorite';
  const compact = searchParams.get('view') === 'compact';
  const favoriteTeamId = favorite?.idEquipo != null &&
    String(favorite.idTemporada) === String(filters.temporada) &&
    String(favorite.idCompeticion) === String(filters.competicion) &&
    String(favorite.idGrupo) === String(filters.grupo) ? String(favorite.idEquipo) : null;
  const [liveTagDialogOpen, setLiveTagDialogOpen] = useState(false);
  const [selectedMatchForLiveTag, setSelectedMatchForLiveTag] = useState(null);
  const [selectedPanelId, setSelectedPanelId] = useState(null);
  const { mutateAsync: createSessionInBackend } = useCreateLiveTagSession();
  const {
    data: userPanels = [],
    isLoading: isLoadingUserPanels,
    isFetching: isFetchingUserPanels,
    refetch: refetchUserPanels,
  } = useButtonPanels({ retry: 1 });
  const availablePanels = useMemo(() => normalizePanelEntries(userPanels), [userPanels]);

  // Obtener jornadas con React Query
  const { data: jornadasData, isLoading, error, refetch } = useJornadas(
    filters.grupo,
    filters.temporada
  );

  const jornadas = useMemo(() => jornadasData?.jornadas || [], [jornadasData]);
  const { data: standings, isLoading: standingsLoading, error: standingsError, refetch: refetchStandings } =
    useClasificacion(filters.temporada, filters.competicion, filters.grupo);
  const latestStandings = useMemo(() => [...(standings || [])]
    .filter((round) => round.clasificacion?.length)
    .sort((a, b) => (getMatchdayNumber(a) || 0) - (getMatchdayNumber(b) || 0)).at(-1), [standings]);

  const handleApplyFilters = () => {
    return Promise.all([refetch(), refetchStandings?.()]);
  };

  // Reset jornada selection when competition/season changes
  const filtersKeyRef = useRef(`${filters.temporada || ''}:${filters.competicion || ''}:${filters.grupo || ''}`);
  useEffect(() => {
    const nextFiltersKey = `${filters.temporada || ''}:${filters.competicion || ''}:${filters.grupo || ''}`;
    if (filtersKeyRef.current === nextFiltersKey) return;
    filtersKeyRef.current = nextFiltersKey;
    autoSelectedRef.current = false;
    setSearchParams({}, { replace: true });
  }, [filters.grupo, filters.temporada, filters.competicion, setSearchParams]);

  const autoSelectedRef = useRef(searchParams.has('jornada'));

  // Auto-select last played jornada only on initial data load
  useEffect(() => {
    if (autoSelectedRef.current || jornadas.length === 0) return;

    const sortedJornadas = [...jornadas].sort(
      (a, b) => (getMatchdayNumber(a) ?? 0) - (getMatchdayNumber(b) ?? 0)
    );

    let lastPlayedNum = null;
    for (let i = sortedJornadas.length - 1; i >= 0; i--) {
      const tieneResultados = sortedJornadas[i].partidos?.some(hasFinalScore);
      if (tieneResultados) {
        lastPlayedNum = getMatchdayNumber(sortedJornadas[i]);
        break;
      }
    }

    if (lastPlayedNum !== null) {
      autoSelectedRef.current = true;
      const jornadaValue = String(lastPlayedNum);
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        nextParams.set('jornada', jornadaValue);
        return nextParams;
      }, { replace: true });
    }
  }, [jornadas, setSearchParams]);

  const handleJornadaChange = (value) => {
    autoSelectedRef.current = true;
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set('jornada', value);
      return nextParams;
    }, { replace: true });
  };

  const handleSearchChange = (value) => {
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      if (value) {
        nextParams.set('q', value);
      } else {
        nextParams.delete('q');
      }
      return nextParams;
    }, { replace: true });
  };

  const setControl = (key, value) => setSearchParams((previous) => {
    const next = new URLSearchParams(previous);
    if (value) next.set(key, value);
    else next.delete(key);
    return next;
  }, { replace: true });

  const matchesTeamSearch = React.useCallback((match) => {
    const query = searchTerm.trim().toLocaleLowerCase();
    return (!onlyFavorite || (favoriteTeamId !== null &&
      [match.codEquipoLocal ?? match.idEquipoLocal, match.codEquipoVisitante ?? match.idEquipoVisitante]
        .some((id) => id != null && String(id) === favoriteTeamId))) &&
      (!query || [match.equipoLocal, match.equipoVisitante]
        .some((name) => name?.toLocaleLowerCase().includes(query)));
  }, [favoriteTeamId, onlyFavorite, searchTerm]);

  const nextPendingRound = useMemo(() => [...jornadas]
    .sort((a, b) => (getMatchdayNumber(a) ?? 0) - (getMatchdayNumber(b) ?? 0))
    .find((round) => {
      const number = getMatchdayNumber(round);
      return number !== null && (selectedJornada === 'all' || number > Number(selectedJornada)) &&
        round.partidos?.some((match) => !hasFinalScore(match) && matchesTeamSearch(match));
    }), [jornadas, matchesTeamSearch, selectedJornada]);

  const openNextPending = () => {
    if (!nextPendingRound) return;
    autoSelectedRef.current = true;
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('jornada', String(getMatchdayNumber(nextPendingRound)));
      next.set('status', 'scheduled');
      return next;
    }, { replace: true });
  };

  const handleBack = () => {
    navigate(window.history.state?.idx > 0 ? -1 : '/dashboard');
  };

  const handleVerPartido = (partido, jornada) => {
    const actaNavigation = buildActaNavigation(
      partido,
      jornada,
      filters.temporada,
      filters.minutosJuego
    );

    if (actaNavigation) {
      navigate(actaNavigation.pathname, { state: actaNavigation.state });
    } else {
      navigate('/informePartido', { state: { partido } });
    }
  };

  const handleLiveTag = (partido, jornada) => {
    if (!partido) {
      navigate('/live-tag');
      return;
    }

    const liveTagMatchId = String(
      partido.idPartido ?? partido.codacta ?? partido.codActa ?? partido.id ??
      `${partido.equipoLocal || 'local'}-${partido.equipoVisitante || 'visitante'}-${partido.fecha || 'sin-fecha'}`
    );

    setSelectedMatchForLiveTag({
      liveTagMatchId,
      idPartido: partido.idPartido ?? partido.codacta ?? partido.codActa ?? null,
      codActa: partido.codacta ?? partido.codActa ?? null,
      idTemporada: partido.idTemporada ?? filters.temporada ?? null,
      temporadaNombre: '',
      categoria: filters.categoriaNombre || '',
      competicion: jornada?.nombreGrupo || '',
      grupo: jornada?.nombreGrupo || '',
      jornada: getMatchdayNumber(jornada) ?? '',
      equipoLocal: partido.equipoLocal ?? 'Local',
      equipoVisitante: partido.equipoVisitante ?? 'Visitante',
      fecha: partido.fecha ?? '',
      hora: partido.hora ?? '',
      estadio: partido.estadio?.trim() || partido.campo?.trim() || '',
      escudoEquipoLocal: partido.escudoEquipoLocal ?? '',
      escudoEquipoVisitante: partido.escudoEquipoVisitante ?? '',
      golesCasa: partido.golesCasa ?? null,
      golesVisitante: partido.golesVisitante ?? null,
    });
    setSelectedPanelId(null);
    setLiveTagDialogOpen(true);
    void refetchUserPanels();
  };

  const handleStartLiveTagSession = async () => {
    if (!selectedMatchForLiveTag) return;

    const selectedPanel = availablePanels.find((panel) => panel.id === selectedPanelId);
    const panelConfig = selectedPanel?.config || null;
    const payload = {
      ...selectedMatchForLiveTag,
      panelConfig,
      panelId: selectedPanelId || null,
      panelName: panelConfig?.name || panelConfig?.nombre || '',
    };

    try {
      const created = await createSessionInBackend(payload);
      const sessionId = created?.id || selectedMatchForLiveTag.liveTagMatchId;
      const matchWithSessionId = { ...payload, sessionId };
      sessionStorage.setItem(LIVE_TAG_STORAGE_KEY, JSON.stringify(matchWithSessionId));
      persistSession(matchWithSessionId);
      navigate(`/live-tag/session/${encodeURIComponent(sessionId)}`, { state: { match: matchWithSessionId } });
    } catch {
      sessionStorage.setItem(LIVE_TAG_STORAGE_KEY, JSON.stringify(payload));
      persistSession(payload);
      navigate(`/live-tag/session/${encodeURIComponent(selectedMatchForLiveTag.liveTagMatchId)}`, { state: { match: payload } });
    }
  };

  const relevantJornadas = useMemo(() => {
    if (jornadas.length === 0) return [];

    const sortedJornadas = [...jornadas].sort(
      (a, b) => (getMatchdayNumber(a) ?? 0) - (getMatchdayNumber(b) ?? 0)
    );

    let lastPlayedIndex = -1;
    for (let i = sortedJornadas.length - 1; i >= 0; i--) {
      const tieneResultados = sortedJornadas[i].partidos?.some(hasFinalScore);
      if (tieneResultados) { lastPlayedIndex = i; break; }
    }

    if (lastPlayedIndex === -1) lastPlayedIndex = 0;

    const selectedIndex = sortedJornadas.findIndex((round) => String(getMatchdayNumber(round)) === selectedJornada);
    const anchor = selectedIndex >= 0 ? selectedIndex : lastPlayedIndex;
    const startIndex = Math.max(0, Math.min(anchor - 1, sortedJornadas.length - 5));
    const endIndex = Math.min(sortedJornadas.length - 1, startIndex + 4);

    const relevant = [];
    for (let i = startIndex; i <= endIndex; i++) {
      if (sortedJornadas[i]) {
        relevant.push({
          ...sortedJornadas[i],
          isCurrent: i === lastPlayedIndex,
          isPast: i < lastPlayedIndex,
          isFuture: i > lastPlayedIndex,
        });
      }
    }
    return relevant;
  }, [jornadas, selectedJornada]);

  const jornadasAgrupadas = useMemo(() => {
    return filterMatchdays(jornadas, selectedJornada).map((jornada) => ({
      ...jornada,
      partidos: (jornada.partidos || []).filter((match) => matchesTeamSearch(match) &&
        (status === 'all' || (status === 'finished' ? hasFinalScore(match) : !hasFinalScore(match)))),
    })).filter((jornada) => jornada.partidos.length > 0);
  }, [jornadas, selectedJornada, matchesTeamSearch, status]);

  return (
    <Box className={`pm-page${compact ? ' pm-page-compact' : ''}`}>
      <header className="pm-heading">
        <div><h1>{t('heroTitle')}</h1><p>{t('introSubtitle')}</p></div>
        <Button variant="contained" startIcon={<EmojiEvents />} onClick={() => navigate('/clasificacion')}>
          {t('standingsShortcut')}
        </Button>
      </header>
      <div className="pm-context-line">
        <IconButton onClick={handleBack} aria-label={t('back', { defaultValue: 'Volver' })}><ArrowBack fontSize="small" /></IconButton>
        <strong>{t('pageTitle')}</strong>
        <span>{[filters.competicionNombre, filters.grupoNombre].filter(Boolean).join(' · ') || t('chooseContext')}</span>
      </div>
      <section className="pm-filter-panel" aria-label={t('seasonContext')}>
        <Filters variant="workspace" onApplyFilters={handleApplyFilters} />
        <div className="pm-filter-hint"><span>{t('filterHierarchy')}</span><span>{t('sharedContext')}</span></div>
      </section>
      <div className="pm-controls">
          <div>
            <label htmlFor="pm-team-search">{t('searchLabel')}</label>
            <TextField
              id="pm-team-search"
              fullWidth
              size="small"
              placeholder={t('search.placeholder')}
              inputProps={{ 'aria-label': t('searchLabel') }}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div>
            <label id="pm-matchday-label">{t('filters.matchday')}</label>
            <FormControl fullWidth size="small">
              <Select
                labelId="pm-matchday-label"
                value={selectedJornada === 'all' || jornadas.some((jornada) =>
                  String(getMatchdayNumber(jornada)) === selectedJornada) ? selectedJornada : ''}
                onChange={(e) => handleJornadaChange(e.target.value)}
              >
                <MenuItem value="all">{t('filters.allMatchdays')}</MenuItem>
                {jornadas.map((jornada) => {
                  const num = getMatchdayNumber(jornada);
                  return (
                    <MenuItem key={num} value={String(num)}>
                      {t('filters.matchdayLabel', { n: num })}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
      </div>

      <div className="pm-quick-controls">
        <ToggleButtonGroup exclusive value={status} aria-label={t('quick.statusLabel')}
          onChange={(_, value) => { if (value) setControl('status', value === 'all' ? null : value); }}>
          <ToggleButton value="all">{t('quick.all')}</ToggleButton>
          <ToggleButton value="finished">{t('quick.finished')}</ToggleButton>
          <ToggleButton value="scheduled">{t('quick.scheduled')}</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButton value="favorite" selected={onlyFavorite} aria-pressed={onlyFavorite}
          disabled={favoriteTeamId === null} title={favoriteTeamId === null ? t('quick.favoriteUnavailable') : favorite?.nombre}
          onChange={() => setControl('team', onlyFavorite ? null : 'favorite')}>{t('quick.favorite')}</ToggleButton>
        <ToggleButtonGroup exclusive value={compact ? 'compact' : 'cards'} aria-label={t('quick.viewLabel')}
          onChange={(_, value) => { if (value) setControl('view', value === 'compact' ? value : null); }}>
          <ToggleButton value="cards">{t('quick.cards')}</ToggleButton>
          <ToggleButton value="compact">{t('quick.compact')}</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="text" disabled={!nextPendingRound} onClick={openNextPending}
          title={!nextPendingRound ? t('quick.noPending') : undefined} endIcon={<ArrowForward />}>{t('quick.nextPending')}</Button>
      </div>
      {onlyFavorite && favoriteTeamId === null && <Alert severity="info">{t('quick.favoriteUnavailable')}</Alert>}

        {!isLoading && relevantJornadas.length > 0 && !searchTerm && (
          <nav className="pm-rounds" aria-label={t('sections.featuredMatchdays')}>
              <Chip
                label={t('filters.allMatchdays')}
                aria-current={selectedJornada === 'all' ? 'true' : undefined}
                color={selectedJornada === 'all' ? 'primary' : 'default'}
                variant={selectedJornada === 'all' ? 'filled' : 'outlined'}
                onClick={() => handleJornadaChange('all')}
                sx={{ cursor: 'pointer' }}
              />
              {relevantJornadas.map((jornada) => {
                const num = getMatchdayNumber(jornada);
                const isSelected = String(num) === String(selectedJornada);
                return (
                  <Chip
                    key={num}
                    label={t('filters.matchdayLabel', { n: num })}
                    aria-current={isSelected ? 'true' : undefined}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => handleJornadaChange(String(num))}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
          </nav>
        )}

      <div className="pm-results-layout">
       <div className="pm-results">
        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={200} />
            ))}
          </Box>
        )}

        {error && (
          <EmptyState
            type="error"
            title={t('errors.title')}
            description={t('errors.description')}
            action={
              <Button variant="contained" onClick={handleApplyFilters}>
                {t('errors.retry')}
              </Button>
            }
          />
        )}

        {!isLoading && !error && jornadasAgrupadas.length === 0 && (
          <EmptyState
            type="no-matches"
            title={t('empty.title')}
            description={t('empty.description')}
            action={<Button onClick={() => {
              autoSelectedRef.current = true;
              setSearchParams(compact ? { jornada: 'all', view: 'compact' } : { jornada: 'all' }, { replace: true });
            }}>{t('quick.clear')}</Button>}
          />
        )}

        {!isLoading && !error && jornadasAgrupadas.length > 0 && (
          <>
            {jornadasAgrupadas.map((jornada) => {
              const num = getMatchdayNumber(jornada);
              const finished = jornada.partidos.filter(hasFinalScore).length;
              return (
              <section className="pm-matchday" key={num} aria-label={t('filters.matchdayLabel', { n: num })}>
                <header><h2>{t('filters.matchdayLabel', { n: num })}</h2>
                  <p>{t('roundSummary', { count: jornada.partidos.length, finished, scheduled: jornada.partidos.length - finished })}</p>
                </header>
                <div className="pm-fixtures-grid">
                  {jornada.partidos.map((partido, index) => <FixtureCard
                    key={partido.idPartido || `${num}-${index}`}
                    match={partido}
                    highlights={getFixtureHighlights(partido, jornada, standings, hasFinalScore(partido))}
                    onDetails={() => handleVerPartido(partido, jornada)}
                    onReport={() => navigate('/informePartido', { state: { partido } })}
                    onLiveTag={() => handleLiveTag(partido, jornada)}
                  />)}
                </div>
              </section>
              );
            })}
          </>
        )}
       </div>
       <aside className="pm-aside">
         <section className="pm-standings" aria-label={t('standingsPreview')}>
           <h2><EmojiEvents />{t('standingsTitle')}</h2>
           <p>{latestStandings ? t('latestStandings', { round: getMatchdayNumber(latestStandings) }) : t('standingsPreview')}</p>
           {standingsLoading ? <Skeleton variant="rectangular" height={220} />
             : latestStandings ? <table>
               <thead><tr><th>#</th><th>{t('team')}</th><th>{t('played')}</th><th>{t('points')}</th></tr></thead>
               <tbody>{latestStandings.clasificacion.slice(0, 5).map((team, index) => <tr
                 key={team.codequipo || index} className={favoriteTeamId && String(team.codequipo) === String(favoriteTeamId) ? 'pm-favorite-row' : undefined}>
                 <td>{team.posicion ?? index + 1}</td><th scope="row">{team.nombre}</th>
                 <td>{team.jugados ?? '—'}</td><td><strong>{team.puntos ?? '—'}</strong></td>
               </tr>)}</tbody>
             </table> : <p role={standingsError ? 'alert' : undefined}>{t(standingsError ? 'standingsError' : 'standingsEmpty')}</p>}
           <Button variant="text" startIcon={<ArrowForward />} onClick={() => navigate('/clasificacion')}>{t('fullStandings')}</Button>
         </section>
         <section className="pm-analysis-note">
           <h3>{t('analysisTitle')}</h3><p>{t('analysisMatches')}</p><p>{t('analysisLiveTag')}</p>
           <strong>{t('analysisActions')}</strong>
         </section>
       </aside>
      </div>
      <Button variant="text" className="pm-mobile-standings" startIcon={<EmojiEvents />} onClick={() => navigate('/clasificacion')}>{t('fullStandings')}</Button>
      <footer className="pm-page-footer"><span>{t('navigationHint')}</span><span>Soccertycs / Partidos</span></footer>

      <Dialog open={liveTagDialogOpen} onClose={() => setLiveTagDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Iniciar Live Tag</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color={theme.palette.text.secondary} sx={{ mb: 2 }}>
            Elige la botonera para arrancar la sesión en este partido futuro o programado.
          </Typography>

          {selectedMatchForLiveTag ? (
            <Card sx={{ mb: 2, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Typography fontWeight={700} color={theme.palette.text.primary}>
                  {selectedMatchForLiveTag.equipoLocal} vs {selectedMatchForLiveTag.equipoVisitante}
                </Typography>
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  {[selectedMatchForLiveTag.competicion, selectedMatchForLiveTag.jornada ? `Jornada ${selectedMatchForLiveTag.jornada}` : null, selectedMatchForLiveTag.fecha, selectedMatchForLiveTag.hora]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </CardContent>
            </Card>
          ) : null}

          <Typography variant="subtitle2" fontWeight={700} color={theme.palette.text.primary} sx={{ mb: 1 }}>
            Seleccione su botonera
          </Typography>

          {isLoadingUserPanels || (isFetchingUserPanels && availablePanels.length === 0) ? (
            <Alert severity="info">Cargando tus botoneras guardadas...</Alert>
          ) : availablePanels.length === 0 ? (
            <Alert severity="info">No tienes botoneras configuradas. Se usará la botonera por defecto.</Alert>
          ) : (
            <Grid container spacing={1.5}>
              {availablePanels.map(({ id, config }) => (
                <Grid item xs={12} key={id}>
                  <Card
                    onClick={() => setSelectedPanelId(id)}
                    sx={{
                      cursor: 'pointer',
                      border: `2px solid ${selectedPanelId === id ? theme.palette.primary.main : theme.palette.divider}`,
                      backgroundColor: selectedPanelId === id ? theme.palette.action.selected : theme.palette.background.paper,
                    }}
                  >
                    <CardContent>
                      <Typography fontWeight={700} color={theme.palette.text.primary}>
                        {config.name || 'Panel'}
                      </Typography>
                      <Typography variant="body2" color={theme.palette.text.secondary}>
                        v{config.version || '1.0'}
                        {config.groups?.length ? ` · ${config.groups.length} grupos` : ''}
                        {countPanelActions(config) ? ` · ${countPanelActions(config)} botones` : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLiveTagDialogOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleStartLiveTagSession} disabled={availablePanels.length > 0 && !selectedPanelId}>
            Iniciar sesión
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartidosPageModern;
